import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import Loader from "../../../components/common/loader/loader";
import {DataTable} from "../../../components/table/data-table";
import {fetchConnectionDocumentBody} from "../services/document-service";
import {FALLBACK_VALUE} from "./document-header-utils";
import {getCustomFormat} from "../../../utils/timeUtils";
import {formatDiscountLabel, normalizeDiscountList} from "./document-body-utils";
import placeholderImage from "../../../resources/images/placeholder.jpeg";
import BoxIcon from "../../../resources/icons/BoxIcon";
import ConnectionDocumentBodyConfirmedQuantityModal from "./connection-document-body-confirmed-quantity-modal";

const formatNumber = (value, fractionDigits = 2) => {
    if (value === null || value === undefined) {
        return FALLBACK_VALUE;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return String(value);
    }
    return numeric.toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
};

const formatDate = (value) => {
    if (!value) {
        return FALLBACK_VALUE;
    }
    try {
        return getCustomFormat(value, "DD/MM/YYYY");
    } catch (error) {
        return FALLBACK_VALUE;
    }
};

const formatDiscountList = (value) => {
    const normalized = normalizeDiscountList(value);
    if (!Array.isArray(normalized) || normalized.length === 0) {
        return FALLBACK_VALUE;
    }
    const formatted = normalized
        .map((entry) => formatDiscountLabel(entry))
        .filter((entry) => entry);
    if (formatted.length === 0) {
        return FALLBACK_VALUE;
    }
    return formatted.join(" + ");
};

const resolveProductPicture = (row) => {
    const directPicture = row?.picture
        ?? row?.product_picture
        ?? row?.product?.picture
        ?? null;

    if (typeof directPicture === "string" && directPicture.trim()) {
        return directPicture;
    }

    if (directPicture && typeof directPicture === "object") {
        return directPicture.reference
            ?? directPicture.url
            ?? directPicture.thumbnail
            ?? null;
    }

    const pictures = Array.isArray(row?.product?.pictures) ? row.product.pictures : [];
    const asset = pictures.find((item) => item?.thumbnail || item?.reference || item?.url);
    return asset?.thumbnail ?? asset?.reference ?? asset?.url ?? null;
};

const ensureProductManufactureReference = (row) => {
    if (!row || typeof row !== "object") {
        return row;
    }
    const productData = row.product && typeof row.product === "object" ? row.product : null;
    const resolvedReference = productData?.manufacture_reference ?? row?.manufacture_reference ?? null;
    const normalizedReference = typeof resolvedReference === "string"
        ? resolvedReference.trim()
        : resolvedReference;
    if (normalizedReference === undefined || normalizedReference === null || normalizedReference === "") {
        return row;
    }
    if (productData?.manufacture_reference === normalizedReference) {
        return row;
    }
    return {
        ...row,
        product: {
            ...(productData ?? {}),
            manufacture_reference: normalizedReference,
        },
    };
};

const EVENT_TYPE_CONFIRMED_QTY = "CONFIRMED_QTY";
const EVENT_TYPE_REQUEST_CONFIRMED_QTY = "REQUEST_CONFIRMED_QUANTITY";

const numericOrNull = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const sumConfirmedQuantities = (events) => {
    if (!Array.isArray(events) || events.length === 0) {
        return 0;
    }
    return events.reduce((total, entry) => {
        const quantityValue = numericOrNull(
            entry?.quantity
            ?? entry?.confirmed_quantity
            ?? entry?.qty
            ?? entry?.value
        );
        return total + (quantityValue ?? 0);
    }, 0);
};

const extractEventsArray = (value) => {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === "object") {
        const candidates = ["events", "data", "items", "rows"];
        for (const key of candidates) {
            if (Array.isArray(value[key])) {
                return value[key];
            }
        }
    }
    return [];
};

const collectRowEvents = (row) => {
    if (!row || typeof row !== "object") {
        return [];
    }
    const sources = [
        row.confirmed_quantity_events,
        row.confirmedQuantityEvents,
        row.document_events,
        row.documentEvents,
        row.events,
        row.body_events,
        row.bodyEvents,
    ];
    const seenIds = new Set();
    const result = [];

    sources.forEach((source) => {
        const list = extractEventsArray(source);
        list.forEach((entry) => {
            if (!entry || typeof entry !== "object") {
                return;
            }
            const eventId = entry.id ?? entry.event_id ?? entry.eventId ?? entry.uuid ?? null;
            if (eventId) {
                if (seenIds.has(eventId)) {
                    return;
                }
                seenIds.add(eventId);
            }
            result.push(entry);
        });
    });

    return result;
};

const getEventType = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return "";
    }
    const rawType = eventItem.type ?? eventItem.event_type ?? eventItem.eventType ?? "";
    const normalizedType = typeof rawType === "string" ? rawType.toUpperCase() : "";
    if (normalizedType === "CONFIRMED_QUANTITY") {
        return EVENT_TYPE_CONFIRMED_QTY;
    }
    return normalizedType;
};

const summarizeEventsList = (list) => {
    const events = Array.isArray(list) ? list : [];
    const summary = {
        events,
        total: events.length,
        confirmedEvents: [],
        confirmedCount: 0,
        confirmedQuantityTotal: 0,
        requestedEvents: [],
        requestedCount: 0,
    };

    events.forEach((eventItem) => {
        const type = getEventType(eventItem);
        if (type === EVENT_TYPE_CONFIRMED_QTY) {
            summary.confirmedEvents.push(eventItem);
            summary.confirmedCount += 1;
            summary.confirmedQuantityTotal += numericOrNull(
                eventItem?.quantity
                ?? eventItem?.confirmed_quantity
                ?? eventItem?.qty
                ?? eventItem?.value
            ) ?? 0;
        } else if (type === EVENT_TYPE_REQUEST_CONFIRMED_QTY) {
            summary.requestedEvents.push(eventItem);
            summary.requestedCount += 1;
        }
    });

    return summary;
};

const summarizeRowEvents = (row) => summarizeEventsList(collectRowEvents(row));
const EMPTY_EVENTS_SUMMARY = summarizeEventsList([]);

const resolveConfirmedQuantityFromRow = (row) => {
    if (!row || typeof row !== "object") {
        return 0;
    }
    const candidates = [
        numericOrNull(row.confirmed_quantity_total),
        numericOrNull(row.confirmedQuantityTotal),
        numericOrNull(row.confirmed_quantity_sum),
        numericOrNull(row.confirmedQuantitySum),
        numericOrNull(row.total_confirmed_quantity),
        numericOrNull(row.totalConfirmedQuantity),
        numericOrNull(row.total_confirmed_qty),
        numericOrNull(row.totalConfirmedQty),
        numericOrNull(row.confirmed_qty_total),
        numericOrNull(row.confirmedQtyTotal),
        numericOrNull(row.confirmed_qty),
        numericOrNull(row.confirmedQty),
        numericOrNull(row.confirmed_quantity),
        numericOrNull(row.confirmedQuantity),
    ];
    const candidateMatch = candidates.find((value) => value !== null);
    if (candidateMatch !== undefined && candidateMatch !== null) {
        return candidateMatch;
    }
    const events = collectRowEvents(row);
    if (events.length > 0) {
        const confirmed = events.filter((entry) => getEventType(entry) === EVENT_TYPE_CONFIRMED_QTY);
        const quantity = sumConfirmedQuantities(confirmed);
        if (quantity > 0) {
            return quantity;
        }
    }
    return 0;
};

const formatConfirmedQuantityBadge = (value, referenceQuantity) => {
    const confirmed = numericOrNull(value);
    const reference = numericOrNull(referenceQuantity);
    if (confirmed === null || confirmed <= 0) {
        return "";
    }
    if (reference === null || reference <= 0) {
        return `${Math.round(confirmed)}%`;
    }
    const percentage = (confirmed / reference) * 100;
    if (!Number.isFinite(percentage) || percentage <= 0) {
        return "";
    }
    return `${Math.round(percentage)}%`;
};

const resolveRowQuantityValue = (row) => {
    if (!row || typeof row !== "object") {
        return null;
    }
    const candidates = [
        numericOrNull(row.quantity),
        numericOrNull(row.qty),
        numericOrNull(row.total_quantity),
        numericOrNull(row.totalQuantity),
    ];
    const match = candidates.find((value) => value !== null);
    if (match !== undefined) {
        return match;
    }
    return null;
};

const resolveQuantityFromSummary = (summary) => {
    if (!summary || typeof summary !== "object") {
        return 0;
    }
    const candidate = numericOrNull(summary.confirmedQuantityTotal);
    if (candidate !== null) {
        return candidate;
    }
    if (Array.isArray(summary.confirmedEvents) && summary.confirmedEvents.length > 0) {
        return sumConfirmedQuantities(summary.confirmedEvents);
    }
    if (Array.isArray(summary.events) && summary.events.length > 0) {
        return sumConfirmedQuantities(
            summary.events.filter((entry) => getEventType(entry) === EVENT_TYPE_CONFIRMED_QTY)
        );
    }
    return 0;
};

const ConnectionDocumentBody = ({companyId, documentId, currencySymbol, onSummaryChange}) => {
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [confirmedQuantityEvents, setConfirmedQuantityEvents] = useState({});
    const [isConfirmedQuantityOpen, setIsConfirmedQuantityOpen] = useState(false);
    const [confirmedQuantityRowId, setConfirmedQuantityRowId] = useState(null);
    const [confirmedQuantityRowQuantity, setConfirmedQuantityRowQuantity] = useState("");
    const [confirmedQuantityRowSummary, setConfirmedQuantityRowSummary] = useState(null);

    useEffect(() => {
        if (!companyId || !documentId) {
            setRows([]);
            setTotalRows(0);
            setIsLoading(false);
            setConfirmedQuantityEvents({});
            setIsConfirmedQuantityOpen(false);
            setConfirmedQuantityRowId(null);
            setConfirmedQuantityRowQuantity("");
            setConfirmedQuantityRowSummary(null);
            return;
        }

        const controller = new AbortController();
        const {signal} = controller;

        const loadBodies = async () => {
            setIsLoading(true);
            setError(null);
            setConfirmedQuantityEvents({});
            try {
                const response = await fetchConnectionDocumentBody({
                    companyId,
                    documentId,
                    signal,
                });
                const bodies = Array.isArray(response?.bodies) ? response.bodies : [];
                const normalizedBodies = bodies.map((item) => ensureProductManufactureReference(item));
                setRows(normalizedBodies);
                setTotalRows(typeof response?.total_rows === "number" ? response.total_rows : 0);
                const summaryMap = normalizedBodies.reduce((accumulator, item) => {
                    const rowId = item?.id ?? item?.body_id ?? item?.bodyId ?? null;
                    if (rowId === null || rowId === undefined) {
                        return accumulator;
                    }
                    const summary = summarizeRowEvents(item);
                    if (
                        summary.total > 0
                        || (summary.confirmedQuantityTotal ?? 0) > 0
                        || (summary.requestedCount ?? 0) > 0
                    ) {
                        accumulator[String(rowId)] = summary;
                    }
                    return accumulator;
                }, {});
                setConfirmedQuantityEvents(summaryMap);
            } catch (err) {
                if (!signal.aborted) {
                    setError(err);
                    setRows([]);
                    setTotalRows(0);
                    setConfirmedQuantityEvents({});
                    setIsConfirmedQuantityOpen(false);
                    setConfirmedQuantityRowId(null);
                    setConfirmedQuantityRowQuantity("");
                    setConfirmedQuantityRowSummary(null);
                }
            } finally {
                if (!signal.aborted) {
                    setIsLoading(false);
                }
            }
        };

        loadBodies();

        return () => controller.abort();
    }, [companyId, documentId]);

    const handleSearch = useCallback((value) => {
        setSearchTerm(typeof value === "string" ? value : "");
    }, []);

    const handleOpenConfirmedQuantity = useCallback((row) => {
        if (!row || typeof row !== "object") {
            return;
        }
        const rowId = row.id ?? row.body_id ?? row.bodyId ?? null;
        if (rowId === null || rowId === undefined) {
            return;
        }
        const rowKey = String(rowId);
        const cachedSummary = confirmedQuantityEvents[rowKey];
        const summary = cachedSummary ?? summarizeRowEvents(row);
        if (!cachedSummary) {
            const hasRelevantData = summary
                && (
                    (numericOrNull(summary?.confirmedQuantityTotal) ?? 0) > 0
                    || (summary?.confirmedCount ?? 0) > 0
                    || (summary?.requestedCount ?? 0) > 0
                    || (Array.isArray(summary?.events) && summary.events.length > 0)
                );
            if (hasRelevantData) {
                setConfirmedQuantityEvents((previous) => ({
                    ...previous,
                    [rowKey]: summary,
                }));
            }
        }
        setConfirmedQuantityRowSummary(summary);
        setConfirmedQuantityRowId(rowId);
        const quantity = resolveRowQuantityValue(row);
        setConfirmedQuantityRowQuantity(quantity === null ? "" : quantity);
        setIsConfirmedQuantityOpen(true);
    }, [confirmedQuantityEvents]);

    const handleCloseConfirmedQuantity = useCallback(() => {
        setIsConfirmedQuantityOpen(false);
        setConfirmedQuantityRowId(null);
        setConfirmedQuantityRowQuantity("");
        setConfirmedQuantityRowSummary(null);
    }, []);

    const handleConfirmedQuantityEventsChange = useCallback((rowId, summary) => {
        const key = rowId !== null && rowId !== undefined ? String(rowId) : "";
        if (!key) {
            return;
        }

        const normalizedSummary = summary ?? summarizeEventsList([]);
        const hasRelevantData = normalizedSummary
            && (
                (numericOrNull(normalizedSummary?.confirmedQuantityTotal) ?? 0) > 0
                || (normalizedSummary?.confirmedCount ?? 0) > 0
                || (normalizedSummary?.requestedCount ?? 0) > 0
            );

        setConfirmedQuantityEvents((previous) => {
            const next = {...previous};
            if (!hasRelevantData) {
                delete next[key];
            } else {
                next[key] = normalizedSummary;
            }
            return next;
        });
        if (rowId === confirmedQuantityRowId) {
            setConfirmedQuantityRowSummary(normalizedSummary);
        }
    }, [confirmedQuantityRowId]);

    const columns = useMemo(() => ([
        {
            id: "position",
            header: t("connection.document.body.position", {defaultValue: "Riga"}),
            accessorKey: "position",
            size: 50,
            enableSorting: false,
            meta: {className: "text-sm text-gray-700"},
        },
        {
            id: "product",
            header: t("connection.document.body.product", {defaultValue: "Prodotto"}),
            size: 400,
            enableSorting: false,
            cell: ({row}) => {
                const name = row.original?.name ?? row.original?.product?.name ?? FALLBACK_VALUE;
                const reference = row.original?.product_reference ?? row.original?.product?.product_reference ?? "";
                const picture = resolveProductPicture(row.original);
                return (
                    <div className="flex items-center gap-3">
                        {picture && (
                            <img
                                src={picture}
                                alt={name}
                                className="h-10 w-10 flex-none rounded-md border border-gray-200 object-cover object-center"
                                onError={(event) => {
                                    event.currentTarget.onerror = null;
                                    event.currentTarget.src = placeholderImage;
                                }}
                            />
                        )}
                        <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">{name}</span>
                            {reference && (
                                <span className="text-xs text-gray-500">{reference}</span>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            id: "manufacture_reference",
            header: t("connection.document.body.manufacture_reference", {defaultValue: "Codice fornitore"}),
            size: 180,
            enableSorting: false,
            meta: {className: "text-sm text-gray-700"},
            cell: ({row}) => {
                const rawSupplierCode = row.original?.product?.manufacture_reference
                    ?? row.original?.manufacture_reference
                    ?? "";
                const supplierCode = rawSupplierCode === null || rawSupplierCode === undefined
                    ? ""
                    : String(rawSupplierCode).trim();
                return (
                    <span className="block text-sm text-gray-900">
                        {supplierCode || FALLBACK_VALUE}
                    </span>
                );
            },
        },
        {
            id: "quantity",
            header: t("connection.document.body.quantity", {defaultValue: "Quantità"}),
            accessorKey: "quantity",
            maxSize: 100,
            meta: {className: "text-right"},
            cell: ({row, getValue}) => {
                const value = getValue();
                const rowId = row.original?.id ?? row.original?.body_id ?? row.original?.bodyId ?? null;
                const rowKey = rowId !== null && rowId !== undefined ? String(rowId) : "";
                const cachedSummary = rowKey ? confirmedQuantityEvents[rowKey] : null;
                let summary = cachedSummary;
                if (!summary) {
                    const derived = summarizeRowEvents(row.original);
                    if (
                        derived.total > 0
                        || (derived.confirmedQuantityTotal ?? 0) > 0
                        || (derived.requestedCount ?? 0) > 0
                    ) {
                        summary = derived;
                    }
                }
                let confirmedQuantity = resolveQuantityFromSummary(summary);
                if (confirmedQuantity === 0) {
                    confirmedQuantity = resolveConfirmedQuantityFromRow(row.original);
                }
                const quantityValue = numericOrNull(row.original?.quantity) ?? numericOrNull(value);
                const badgeValue = formatConfirmedQuantityBadge(confirmedQuantity, quantityValue);
                const hasRequestedEvents = summary ? (summary.requestedCount ?? 0) > 0 : false;
                const hasEvents = Boolean(badgeValue) || hasRequestedEvents;
                const buttonTitle = t("connection.document.confirmed_quantity.button_title", {defaultValue: "Dettagli quantità confermate"});
                const canOpenModal = Boolean(rowKey);
                return (
                    <div className="flex items-center justify-end gap-2">
                        <span className="text-sm text-gray-900">
                            {formatNumber(value, 0)}
                        </span>
                        {canOpenModal ? (
                            <button
                                type="button"
                                onClick={() => handleOpenConfirmedQuantity(row.original)}
                                title={buttonTitle}
                                aria-label={buttonTitle}
                                className="relative inline-flex items-center justify-center rounded-md border border-transparent bg-white px-1.5 py-1 text-gray-600 transition hover:border-blue-400 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            >
                                <span className="pointer-events-none relative inline-flex">
                                    <BoxIcon className={hasEvents ? "h-5 w-5 text-green-600" : "h-5 w-5"} aria-hidden="true" />
                                    {hasEvents && badgeValue ? (
                                        <span className="absolute -top-1.5 -right-2 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-green-600 px-1 text-[10px] font-semibold text-white">
                                            {badgeValue}
                                        </span>
                                    ) : null}
                                    {hasEvents && !badgeValue ? (
                                        <span className="absolute -top-1.5 -right-2 inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-green-600" />
                                    ) : null}
                                </span>
                            </button>
                        ) : null}
                    </div>
                );
            },
        },
        {
            id: "unit_price",
            header: t("connection.document.body.unit_price", {defaultValue: "Prezzo unitario"}),
            accessorKey: "unit_price",
            maxSize: 100,
            meta: {className: "text-right"},
            cell: ({getValue}) => (
                <span className="block text-right text-sm text-gray-900">
                    {formatNumber(getValue())}
                    {currencySymbol ? ` ${currencySymbol}` : ""}
                </span>
            ),
        },
        {
            id: "unit_price_discounts",
            header: t("connection.document.body.unit_price_discounts", {defaultValue: "Sconti prezzo unit."}),
            accessorKey: "unit_price_discounts",
            size: 100,
            maxSize: 100,
            meta: {className: "text-sm text-gray-700"},
            cell: ({row}) => {
                const value = row.original?.unit_price_discounts ?? row.original?.discount_list ?? [];
                return (
                    <span className="block text-sm text-gray-900">
                        {formatDiscountList(value)}
                    </span>
                );
            },
        },
        {
            id: "total_net_price",
            header: t("connection.document.body.total", {defaultValue: "Totale netto"}),
            accessorKey: "total_net_price",
            size: 100,
            meta: {className: "text-right"},
            cell: ({getValue}) => (
                <span className="block text-right text-sm text-gray-900">
                    {formatNumber(getValue())}
                    {currencySymbol ? ` ${currencySymbol}` : ""}
                </span>
            ),
        },
        {
            id: "delivery_date",
            header: t("connection.document.body.delivery_date", {defaultValue: "Data consegna"}),
            accessorKey: "delivery_date",
            size: 150,
            meta: {className: "text-sm text-gray-700"},
            cell: ({getValue}) => (
                <span className="block text-sm text-gray-900">
                    {formatDate(getValue())}
                </span>
            ),
        },
    ]), [confirmedQuantityEvents, currencySymbol, handleOpenConfirmedQuantity, t]);

    const filteredRows = useMemo(() => {
        const term = (searchTerm ?? "").toLowerCase();
        if (!term) {
            return rows;
        }
        return rows.filter((row) => {
            const candidates = [
                row?.name,
                row?.product_reference,
                row?.product?.product_reference,
                row?.manufacture_reference,
                row?.product?.manufacture_reference,
                row?.product?.name,
            ];
            return candidates.some((value) => (
                typeof value === "string"
                && value.toLowerCase().includes(term)
            ));
        });
    }, [rows, searchTerm]);

    const totals = useMemo(() => filteredRows.reduce((accumulator, row) => ({
        quantity: accumulator.quantity + (Number(row?.quantity) || 0),
        totalNet: accumulator.totalNet + (Number(row?.total_net_price) || 0),
    }), {quantity: 0, totalNet: 0}), [filteredRows]);
    const rowCount = rows.length;
    const filteredCount = filteredRows.length;
    const totalQuantity = totals.quantity;
    const totalNet = totals.totalNet;

    useEffect(() => {
        if (typeof onSummaryChange !== "function") {
            return;
        }
        const resolvedApiTotal = typeof totalRows === "number" ? totalRows : null;
        onSummaryChange({
            totalRows: resolvedApiTotal,
            displayRowCount: resolvedApiTotal ?? rowCount,
            filteredRowCount: filteredCount,
            totalQuantity,
            totalNet,
            isLoading,
            hasError: Boolean(error),
        });
    }, [onSummaryChange, totalRows, rowCount, filteredCount, totalQuantity, totalNet, isLoading, error]);

    useEffect(() => () => {
        if (typeof onSummaryChange === "function") {
            onSummaryChange({
                totalRows: null,
                displayRowCount: 0,
                filteredRowCount: 0,
                totalQuantity: 0,
                totalNet: 0,
                isLoading: false,
                hasError: false,
            });
        }
    }, [onSummaryChange]);

    if (isLoading && rows.length === 0) {
        return (
            <div className="flex justify-center py-12">
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-6 text-center text-sm text-rose-700">
                {t("connection.document.body.load_error", {defaultValue: "Impossibile caricare le righe del documento."})}
            </div>
        );
    }

    return (
        <>
            <div className="space-y-4">
                <div className="overflow-hidden bg-white">
                    <DataTable
                        data={filteredRows}
                        columns={columns}
                        isLoading={isLoading}
                        emptyStateTitle={t("app.common.no_data", {defaultValue: "Nessun dato disponibile"})}
                        listKey={`connection-document-body-${documentId}`}
                        enableScroll={false}
                        onSearch={handleSearch}
                        activePagination={false}
                        stickyHeader
                    />
                </div>
            </div>
            {isConfirmedQuantityOpen && confirmedQuantityRowId && (
                <ConnectionDocumentBodyConfirmedQuantityModal
                    isOpen={isConfirmedQuantityOpen}
                    onClose={handleCloseConfirmedQuantity}
                    companyId={companyId}
                    documentId={documentId}
                    bodyId={confirmedQuantityRowId}
                    rowQuantity={confirmedQuantityRowQuantity}
                    initialSummary={confirmedQuantityRowSummary ?? EMPTY_EVENTS_SUMMARY}
                    onEventsChange={(summary) => handleConfirmedQuantityEventsChange(confirmedQuantityRowId, summary)}
                />
            )}
        </>
    );
};

ConnectionDocumentBody.propTypes = {
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currencySymbol: PropTypes.string,
    onSummaryChange: PropTypes.func,
};

ConnectionDocumentBody.defaultProps = {
    companyId: null,
    documentId: null,
    currencySymbol: "",
    onSummaryChange: undefined,
};

export default ConnectionDocumentBody;
